import React, { useContext, useEffect, useState } from 'react';
import { cloneDeep } from 'lodash';

import { RestApiContext } from '../../contexts/RestApiContext';
import { PluginContext } from '../../contexts';

import {
    FileType,
    useAggregatedState,
    usePageReadyState,
    useBusyState,
    updateBundleItem,
    removeBundleItem
} from '../../../helpers';

export const PageContext = React.createContext(null);

const { Provider } = PageContext;
export const PageProvider = ({ ...props }) => {
    const [pageIsReady, pageHasError, setPageIsReady] = usePageReadyState({});
    const [busy, isBusy, setIsBusy] = useBusyState({});
    const [state, setState] = useAggregatedState({
        license: null,
        isLegacy: false,
        settings: null,
        appConfig: null,
        builds: null,
        buildStatus: null,
        buildError: null
    });

    const {
        plugin
    } = useContext(PluginContext);

    const apiClient = useContext(RestApiContext);

    const checkLicenseAsync = async () => {
        try {
            const license = await apiClient.checkLicenseAsync();
            setState({ license });
        } catch (e) {
            console.debug(e);
        }
    }

    const getSettingsAsync = async () => {
        try {
            const settings = await apiClient.getSettingsAsync();
            const areLegacySettings = settings["AppID"] !== undefined;
            setState({
                settings: areLegacySettings ? {} : settings,
                isLegacy: areLegacySettings
            });
        } catch (e) {
            console.debug(e);
        }
    }

    const getAppConfigAsync = async () => {
        try {
            const appConfig = await apiClient.getAppConfigAsync();
            setState({ appConfig: appConfig ?? {} });
        } catch (e) {
            console.debug(e);
        }
    }

    const getBuildsAsync = async () => {
        try {
            const buildList = await apiClient.getBuildsAsync();
            setState({ builds: buildList });
        } catch (e) {
            console.debug(e);
        }
    }

    const createBuildAsync = async () => {
        try {
            const { settings } = state;
            const buildConfig = {
                Version: 1,
                iOSBuildEnabled     : settings.enableiOSBuild,
                AndroidBuildEnabled : settings.enableAndroidBuild,
                iOS: {
                    p12DownloadUrl: settings.iOSP12CertificateFile,
                    p12FileName: `${FileType.iOSP12Certificate}.p12`,
                    p12Password: settings.iOSCertificatePassword,
                    provisionDownloadUrl: settings.iOSMobileProvisionFile,
                    provisionFileName: `${FileType.iOSMobileProvisioning}.mobileprovision`
                },
                android: {
                    keyStoreDownloadUrl: settings.androidKeyStoreFile,
                    keyStoreFileName: `${FileType.AndroidKeyStore}.keystore`,
                    keyAlias: settings.androidKeyStoreAlias,
                    keyPassword: settings.androidKeyStorePassword
                },
                title : settings.appTitle,
                bundleID: settings.bundleId,
                versionName: settings.versionNumber,
                appIconDownloadUrl: settings.appIcon,
            }
            setState({ buildStatus: null, buildError: null });
            const buildStatus = await apiClient.createBuildAsync(buildConfig);
            if (buildStatus) {
                setState({ buildStatus });
                return buildStatus;
            }
        } catch (e) {
            console.debug(e);
            setState({ buildStatus: null, buildError: e.message });
            return null;
        }
    }

    return (
        <Provider value={{
            state,
            pageIsReady,
            pageHasError,
            setPageIsReady,
            isBusy,
            setIsBusy,
            checkLicenseAsync,
            getSettingsAsync,
            getAppConfigAsync,
            getBuildsAsync,
            createBuildAsync
        }}>
            {props.children}
        </Provider>
    );
}